import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { Info, Gamepad, Award, Zap } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const Vex5 = () => {
  const intl = useIntl();

  return (
    <div className="flex flex-col min-h-screen bg-gray-900 text-white">
      <Helmet>
        <title>Play Vex 5 Online | Free Platform Game | Pokedle</title>
        <meta name="description" content="Play Vex 5 online for free - Master challenging obstacles, traps, and platforming puzzles. Enjoy the ultimate stick-figure platform adventure game." />
        <meta name="keywords" content="Vex 5, platform game, online games, stick figure game, challenging platformer" />
        <link rel="canonical" href="https://pokedle.com/vex5" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-indigo-800 to-blue-900 opacity-80"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMjIiIGZpbGwtb3BhY2l0eT0iLjA1Ij48cGF0aCBkPSJNMzYgMzBoLTZWMGg2djMwWk0wIDMwaDZWMEgwdjMwWiIvPjwvZz48L2c+PC9zdmc+')] opacity-10"></div>
        
        <div className="container relative mx-auto px-8 text-center">
          <h1 className="text-5xl md:text-7xl font-extrabold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-600 animate-pulse">
            Vex 5
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
            Master the ultimate platforming challenge with precision jumps, deadly traps, and exciting obstacles
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <div className="flex items-center bg-indigo-800 bg-opacity-50 backdrop-blur-sm p-3 rounded-lg">
              <Gamepad className="h-6 w-6 mr-2 text-indigo-400" />
              <span>Platform Game</span>
            </div>
            <div className="flex items-center bg-indigo-800 bg-opacity-50 backdrop-blur-sm p-3 rounded-lg">
              <Award className="h-6 w-6 mr-2 text-indigo-400" />
              <span>80+ Challenging Levels</span>
            </div>
            <div className="flex items-center bg-indigo-800 bg-opacity-50 backdrop-blur-sm p-3 rounded-lg">
              <Zap className="h-6 w-6 mr-2 text-indigo-400" />
              <span>Fast-paced Action</span>
            </div>
          </div>
          <a href="#play-now" className="inline-block bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-bold py-3 px-8 rounded-full transform transition-transform hover:scale-105 animate-bounce">
            Play Now
          </a>
        </div>
      </section>

      {/* Game Section */}
      <section id="play-now" className="py-12 bg-gray-900">
        <div className="container mx-auto px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">Play Vex 5 Online</h2>
          <div className="rounded-xl overflow-hidden shadow-2xl border-4 border-indigo-700 bg-black">
            <div className="aspect-[16/9] md:aspect-[4/3] lg:aspect-[16/9] h-[500px] md:h-[600px]">
              <iframe 
                src="https://vex5online.github.io/file/" 
                className="w-full h-full" 
                frameBorder="0" 
                allow="gamepad *;"
                title="Vex 5 Game"
                loading="lazy"
              ></iframe>
            </div>
          </div>
          
          <div className="mt-8 text-center">
            <p className="text-gray-400 italic">Use keyboard controls: Arrow keys to move, Space to jump, and Shift to slide.</p>
          </div>
        </div>
      </section>

      {/* Game Info */}
      <section className="py-16 bg-gray-900 bg-opacity-90">
        <div className="container mx-auto px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center border-b-2 border-indigo-600 pb-4">About Vex 5</h2>
            
            <div className="space-y-6 text-gray-300">
              <p>
                Vex 5 is an exhilarating and challenging online platform game that takes players on a thrilling journey through a series of obstacle courses filled with traps, spikes, and other hazardous elements. Developed by Amazing Adam, this fifth installment builds upon the success of its predecessors, offering an enhanced gaming experience with improved graphics, level design, and overall gameplay mechanics.
              </p>
              
              <h3 className="text-2xl font-semibold mt-8 text-indigo-400">The Evolution of Vex</h3>
              <p>
                The Vex series has evolved significantly since its inception, with each new release introducing innovative features and improvements. Vex 5 represents the pinnacle of this evolution, incorporating feedback from the gaming community to deliver a polished and engaging platforming experience. The game retains its signature minimalist stick-figure aesthetic while introducing more complex level designs and challenges.
              </p>
              
              <p>
                What sets Vex 5 apart from other platform games is its perfect balance of accessibility and challenge. The controls are intuitive and responsive, allowing players to execute precise movements with ease. However, mastering the game requires dedication, as later levels introduce increasingly difficult obstacles that test players' reflexes, timing, and problem-solving abilities.
              </p>
              
              <div className="grid md:grid-cols-2 gap-8 my-12">
                <div className="bg-gradient-to-br from-indigo-900 to-purple-900 p-6 rounded-xl">
                  <h4 className="text-xl font-bold mb-4 text-indigo-300">Gameplay Features</h4>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>80+ meticulously crafted levels</li>
                    <li>Responsive and precise controls</li>
                    <li>Variety of obstacles and traps</li>
                    <li>Checkpoint system for progression</li>
                    <li>Collectible stars for completionists</li>
                    <li>Time trials for additional challenge</li>
                  </ul>
                </div>
                <div className="bg-gradient-to-br from-blue-900 to-indigo-900 p-6 rounded-xl">
                  <h4 className="text-xl font-bold mb-4 text-blue-300">Game Mechanics</h4>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>Wall jumping for vertical traversal</li>
                    <li>Sliding to avoid overhead obstacles</li>
                    <li>Momentum-based swinging</li>
                    <li>Gravity-defying sections</li>
                    <li>Moving platforms that require timing</li>
                    <li>Environmental hazards like spikes and lasers</li>
                  </ul>
                </div>
              </div>
              
              <h3 className="text-2xl font-semibold mt-8 text-indigo-400">Level Design Excellence</h3>
              <p>
                One of the standout features of Vex 5 is its exceptional level design. Each level is a carefully constructed puzzle that introduces new challenges while building upon previously learned skills. Early levels serve as tutorials, introducing basic mechanics in a controlled environment. As players progress, these mechanics are combined in increasingly complex ways, creating a satisfying difficulty curve.
              </p>
              
              <p>
                The environments in Vex 5 are diverse, ranging from icy landscapes to fiery dungeons, industrial complexes to abstract dimensions. These diverse settings not only contribute to the game's visual appeal but also introduce unique obstacles and mechanics tied to the environment. Ice surfaces affect traction, while fiery areas may contain timing-based flame jets that require precise movement to avoid.
              </p>
              
              <h3 className="text-2xl font-semibold mt-8 text-indigo-400">A Challenging Journey</h3>
              <p>
                Vex 5 is designed to challenge players of all skill levels. The difficulty progression is carefully balanced, ensuring that each new level feels conquerable while still providing a sense of achievement upon completion. The game employs a checkpoint system that allows players to progress through difficult sections incrementally, reducing frustration while maintaining the challenge.
              </p>
              
              <p>
                For completionists, each level contains collectible stars placed in particularly challenging locations. Gathering these stars requires mastery of the game's mechanics and often necessitates exploring alternative routes through levels. This additional objective adds replay value and provides an extra layer of challenge for skilled players.
              </p>
              
              <h3 className="text-2xl font-semibold mt-8 text-indigo-400">Community and Competition</h3>
              <p>
                Beyond its single-player experience, Vex 5 fosters a sense of community and competition through its time trial system. Players can compete to complete levels in the fastest time possible, with global leaderboards showcasing the best performances. This competitive aspect has led to the emergence of speedrunning communities dedicated to finding optimal routes and techniques for each level.
              </p>
              
              <p>
                The game's accessibility has made it a popular choice for content creators on platforms like YouTube and Twitch, further expanding its reach and community. Watching skilled players navigate the most challenging levels can be both entertaining and educational, providing insights into advanced techniques and strategies.
              </p>
              
              <h3 className="text-2xl font-semibold mt-8 text-indigo-400">Technical Excellence</h3>
              <p>
                From a technical standpoint, Vex 5 excels in several areas. The physics system is responsive and consistent, allowing players to predict how their character will move and interact with the environment. This predictability is crucial for a platform game, where precision is often the difference between success and failure.
              </p>
              
              <p>
                The game's visual style, while minimalist, is effective in communicating important information. Hazards are clearly distinguishable from safe surfaces, and the stick figure protagonist stands out against the background, ensuring players can always track their position. The animations are smooth and fluid, enhancing the overall gameplay experience.
              </p>
              
              <p>
                The soundtrack complements the action perfectly, with energetic tracks that enhance the sense of urgency without becoming distracting. Sound effects provide important feedback for player actions, from the satisfying "ping" of collecting a star to the ominous hum of an approaching laser.
              </p>
              
              <h3 className="text-2xl font-semibold mt-8 text-indigo-400">Why Vex 5 Stands Out</h3>
              <p>
                In a market saturated with platform games, Vex 5 distinguishes itself through its commitment to core gameplay principles. Rather than relying on flashy graphics or gimmicks, it focuses on delivering a pure platforming experience that rewards skill, patience, and determination. This focus on fundamentals has earned it a dedicated following among platform game enthusiasts.
              </p>
              
              <p>
                The game's browser-based nature makes it accessible to players regardless of their hardware capabilities. Unlike resource-intensive AAA titles, Vex 5 runs smoothly on most devices, from high-end gaming rigs to modest laptops. This accessibility has contributed significantly to its popularity, allowing a wide audience to experience its challenging gameplay.
              </p>
              
              <p>
                Whether you're a seasoned platform game veteran looking for your next challenge or a casual gamer seeking an engaging experience, Vex 5 offers something for everyone. Its combination of accessible controls, challenging level design, and satisfying progression makes it a standout title in the browser game landscape.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How to Play Section */}
      <section className="py-16 bg-indigo-900 bg-opacity-30">
        <div className="container mx-auto px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">How to Play Vex 5</h2>
          
          <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
            <div className="bg-gray-800 bg-opacity-70 p-6 rounded-xl shadow-lg">
              <h3 className="text-xl font-bold mb-4 text-indigo-300">Basic Controls</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="bg-indigo-700 text-white text-sm px-2 py-1 rounded mr-3 mt-1">←→</span>
                  <span>Use left and right arrow keys to move your character</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-indigo-700 text-white text-sm px-2 py-1 rounded mr-3 mt-1">SPACE</span>
                  <span>Press space bar to jump. Hold longer for higher jumps</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-indigo-700 text-white text-sm px-2 py-1 rounded mr-3 mt-1">SHIFT</span>
                  <span>Hold shift to slide under obstacles</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-indigo-700 text-white text-sm px-2 py-1 rounded mr-3 mt-1">R</span>
                  <span>Press R to restart from the last checkpoint</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-gray-800 bg-opacity-70 p-6 rounded-xl shadow-lg">
              <h3 className="text-xl font-bold mb-4 text-blue-300">Advanced Techniques</h3>
              <ul className="space-y-4">
                <li>
                  <span className="font-semibold text-indigo-300">Wall Jumping:</span>
                  <p className="mt-1">When against a wall, press jump to perform a wall jump and reach higher areas.</p>
                </li>
                <li>
                  <span className="font-semibold text-indigo-300">Momentum Control:</span>
                  <p className="mt-1">Master your momentum by timing your jumps and controlling your aerial movement.</p>
                </li>
                <li>
                  <span className="font-semibold text-indigo-300">Slide Jumps:</span>
                  <p className="mt-1">Slide into a jump for extra distance and to navigate tight spaces quickly.</p>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="max-w-4xl mx-auto mt-12 bg-gray-800 bg-opacity-70 p-6 rounded-xl shadow-lg">
            <h3 className="text-xl font-bold mb-4 text-purple-300">Tips for Success</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-700 bg-opacity-50 p-4 rounded-lg">
                <p className="font-semibold">⭐ Take your time with new obstacles</p>
                <p className="text-sm text-gray-300 mt-1">Observe patterns before attempting difficult jumps.</p>
              </div>
              <div className="bg-gray-700 bg-opacity-50 p-4 rounded-lg">
                <p className="font-semibold">⭐ Use checkpoints strategically</p>
                <p className="text-sm text-gray-300 mt-1">Don't rush past them if a difficult section is ahead.</p>
              </div>
              <div className="bg-gray-700 bg-opacity-50 p-4 rounded-lg">
                <p className="font-semibold">⭐ Practice timing-based obstacles</p>
                <p className="text-sm text-gray-300 mt-1">Many later levels require precise timing rather than speed.</p>
              </div>
              <div className="bg-gray-700 bg-opacity-50 p-4 rounded-lg">
                <p className="font-semibold">⭐ Look for hidden routes</p>
                <p className="text-sm text-gray-300 mt-1">Some stars are only accessible through secret paths.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-900">
        <div className="container mx-auto px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center flex items-center justify-center">
              <Info className="mr-3 h-8 w-8 text-indigo-400" />
              Frequently Asked Questions
            </h2>
            
            <Accordion type="single" collapsible className="bg-gray-800 bg-opacity-50 rounded-xl overflow-hidden">
              <AccordionItem value="item-1" className="border-b border-gray-700">
                <AccordionTrigger className="px-6 py-4 hover:bg-gray-700 text-left font-medium">
                  What is Vex 5?
                </AccordionTrigger>
                <AccordionContent className="px-6 py-4 bg-gray-800 text-gray-300">
                  Vex 5 is a challenging platform game where players control a stick figure character navigating through obstacle courses filled with traps, spikes, and various hazards. It's the fifth installment in the popular Vex series, featuring improved graphics, level design, and gameplay mechanics compared to its predecessors.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-2" className="border-b border-gray-700">
                <AccordionTrigger className="px-6 py-4 hover:bg-gray-700 text-left font-medium">
                  How do I play Vex 5?
                </AccordionTrigger>
                <AccordionContent className="px-6 py-4 bg-gray-800 text-gray-300">
                  Use arrow keys to move left and right, space bar to jump, and shift to slide. You'll need to navigate through levels by avoiding obstacles, timing your jumps, and solving platforming puzzles. The goal is to reach the end of each level while collecting stars for additional challenges.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-3" className="border-b border-gray-700">
                <AccordionTrigger className="px-6 py-4 hover:bg-gray-700 text-left font-medium">
                  How many levels are in Vex 5?
                </AccordionTrigger>
                <AccordionContent className="px-6 py-4 bg-gray-800 text-gray-300">
                  Vex 5 features over 80 challenging levels that progressively increase in difficulty. Each level introduces new obstacles and challenges, requiring players to master different skills and techniques as they progress through the game.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-4" className="border-b border-gray-700">
                <AccordionTrigger className="px-6 py-4 hover:bg-gray-700 text-left font-medium">
                  Is Vex 5 free to play?
                </AccordionTrigger>
                <AccordionContent className="px-6 py-4 bg-gray-800 text-gray-300">
                  Yes, Vex 5 is free to play on our website. You can enjoy the full game without any paywall or premium features. Simply load the game and start playing immediately without registration or download.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-5" className="border-b border-gray-700">
                <AccordionTrigger className="px-6 py-4 hover:bg-gray-700 text-left font-medium">
                  Who developed Vex 5?
                </AccordionTrigger>
                <AccordionContent className="px-6 py-4 bg-gray-800 text-gray-300">
                  Vex 5 was developed by Amazing Adam (Kiz10), who created the entire Vex series. The game has gained popularity for its challenging gameplay, precise controls, and well-designed levels.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-6" className="border-b border-gray-700">
                <AccordionTrigger className="px-6 py-4 hover:bg-gray-700 text-left font-medium">
                  Can I play Vex 5 on mobile devices?
                </AccordionTrigger>
                <AccordionContent className="px-6 py-4 bg-gray-800 text-gray-300">
                  While our embedded version is optimized for desktop gameplay, some mobile browsers may support it. However, the game was designed with keyboard controls in mind, so the experience is best on desktop computers where you can use arrow keys, space, and shift for precise control.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-7" className="border-b border-gray-700">
                <AccordionTrigger className="px-6 py-4 hover:bg-gray-700 text-left font-medium">
                  How do I get better at Vex 5?
                </AccordionTrigger>
                <AccordionContent className="px-6 py-4 bg-gray-800 text-gray-300">
                  Practice is key! Start by mastering the basic controls, then focus on timing and precision. Watch your character's movements carefully and learn from your mistakes. Many players find it helpful to observe an obstacle's pattern before attempting to overcome it. Don't be afraid to restart from checkpoints when learning difficult sections.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-8" className="border-b border-gray-700">
                <AccordionTrigger className="px-6 py-4 hover:bg-gray-700 text-left font-medium">
                  What makes Vex 5 different from previous Vex games?
                </AccordionTrigger>
                <AccordionContent className="px-6 py-4 bg-gray-800 text-gray-300">
                  Vex 5 builds upon its predecessors with improved graphics, more complex level designs, and refined gameplay mechanics. It introduces new obstacles, environmental hazards, and platforming challenges while maintaining the core stick-figure aesthetic and precise controls that fans love. The difficulty curve is also more balanced, making it accessible to newcomers while still challenging for veterans.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Related Games */}
      <section className="py-16 bg-indigo-900 bg-opacity-20">
        <div className="container mx-auto px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">You Might Also Enjoy</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gray-800 rounded-xl overflow-hidden shadow-lg transform transition-transform hover:scale-105">
              <div className="h-48 bg-gradient-to-r from-purple-800 to-indigo-800 flex items-center justify-center">
                <h3 className="text-2xl font-bold">PokéRogue</h3>
              </div>
              <div className="p-6">
                <p className="text-gray-300 mb-4">Experience turn-based Pokémon battles with roguelike elements and strategic gameplay.</p>
                <a href="/pokerogue" className="inline-block bg-indigo-700 hover:bg-indigo-800 text-white font-semibold py-2 px-4 rounded-lg">Play Now</a>
              </div>
            </div>
            
            <div className="bg-gray-800 rounded-xl overflow-hidden shadow-lg transform transition-transform hover:scale-105">
              <div className="h-48 bg-gradient-to-r from-blue-800 to-cyan-800 flex items-center justify-center">
                <h3 className="text-2xl font-bold">Stickman Hook</h3>
              </div>
              <div className="p-6">
                <p className="text-gray-300 mb-4">Swing through challenging levels with a grappling hook in this physics-based platformer.</p>
                <a href="/stickman-hook" className="inline-block bg-indigo-700 hover:bg-indigo-800 text-white font-semibold py-2 px-4 rounded-lg">Play Now</a>
              </div>
            </div>
            
            <div className="bg-gray-800 rounded-xl overflow-hidden shadow-lg transform transition-transform hover:scale-105">
              <div className="h-48 bg-gradient-to-r from-green-800 to-yellow-700 flex items-center justify-center">
                <h3 className="text-2xl font-bold">Monkey Mart</h3>
              </div>
              <div className="p-6">
                <p className="text-gray-300 mb-4">Build and manage your own supermarket as a cute monkey in this business simulation game.</p>
                <a href="/monkey-mart" className="inline-block bg-indigo-700 hover:bg-indigo-800 text-white font-semibold py-2 px-4 rounded-lg">Play Now</a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Vex5;
